import { saveInstanceId, getSavedInstanceId } from "./authService";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const INSTANCE_STORAGE_KEY = "sky-selected-instance-id";

// Routes where UUID path segments are resource IDs, NOT instance IDs
const RESOURCE_ID_ROUTES = [
  "/surveys/",
  "/locations/",
  "/operators/",
  "/owners/",
  "/asa/",
  "/jobs/",
  "/applicants/",
  "/portals/",
  "/career-portals/",
  "/packets/",
  "/templates/",
  "/campaigns/",
  "/forms/",
  // /document-centre/:applicationId (an application id is a UUID)
  "/document-centre/",
  // /onboarding/cases/:id (a case id is a UUID)
  "/hris/cases/",
  // the old path, redirected to the above
  "/onboarding/cases/",
  // skyPerformance E1: /performance/coaching/records/:recordId
  "/performance/coaching/records/",
  // skyPerformance E2: /performance/file/:personKey (a roster key may look like anything)
  "/performance/file/",
  // skyPerformance E3.a: /performance/plans/:planId (perf_plans.id is a UUID)
  "/performance/plans/",
];

export function clearStoredInstanceId(): void {
  try {
    localStorage.removeItem(INSTANCE_STORAGE_KEY);
  } catch {
    // Ignore localStorage errors (e.g., private browsing mode)
  }
}

/** The instance the address bar names: the query's id, else a trailing path UUID. Stores nothing. */
function instanceIdInUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const queryInstanceId = urlParams.get("instanceid") || urlParams.get("instanceId");
  if (queryInstanceId && UUID_REGEX.test(queryInstanceId)) {
    return queryInstanceId;
  }

  // Check if we're on a route where the UUID is a resource ID (not instance ID)
  const pathname = window.location.pathname;
  const isResourceIdRoute = RESOURCE_ID_ROUTES.some((route) => pathname.startsWith(route));

  // Only extract instance ID from path if not on a resource ID route
  if (!isResourceIdRoute) {
    const pathSegments = pathname.split("/").filter(Boolean);
    if (pathSegments.length > 0) {
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (UUID_REGEX.test(lastSegment)) {
        return lastSegment;
      }
    }
  }

  return null;
}

export function getInstanceIdFromUrl(): string | null {
  const inUrl = instanceIdInUrl();
  if (inUrl) {
    saveInstanceId(inUrl);
    return inUrl;
  }

  const storedInstanceId = getSavedInstanceId();
  if (storedInstanceId && UUID_REGEX.test(storedInstanceId)) {
    return storedInstanceId;
  }

  return null;
}

/**
 * The instance getInstanceIdFromUrl would find, without saving it: for code that only reads while
 * it draws (the loading screen). Storage that throws reads as no saved instance.
 */
export function peekInstanceId(): string | null {
  const inUrl = instanceIdInUrl();
  if (inUrl) return inUrl;
  try {
    const stored = localStorage.getItem(INSTANCE_STORAGE_KEY);
    return stored && UUID_REGEX.test(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function getInstanceParam(): string {
  const instanceId = getInstanceIdFromUrl();
  return instanceId ? `instanceId=${instanceId}` : "";
}
